import { encerrarSessao } from "./sessao";
import { useCallback, useEffect, useState } from "react";
import { TABS } from "./config";
import { useFaixaDeAbas } from "./faixa-abas";
import type { Refs, Row } from "./types";
import { api, setSessionUser } from "./api";
import { PermissoesProvider } from "./permissoes";
import { ordenarPorNome } from "./ordem";
import { ICONS, IconDashboard, IconCalendar, IconSun, IconMoon, IconLogout, IconHome } from "./icons";
import AnimatedBackground from "./components/AnimatedBackground";
import SolicitarAtualizacaoView from "./components/SolicitarAtualizacaoView";
import { AvisoReuniao } from "./components/PedirReuniaoView";
import AvisoSistema from "./components/AvisoSistema";
import Login from "./components/Login";
import logoBranca from "./assets/logo-branca.png";
import { applyTheme, getInitialTheme, type Theme } from "./theme";
import Dashboard from "./components/Dashboard";
import AgendaView from "./components/AgendaView";
import AtividadesView from "./components/AtividadesView";
import CrudView from "./components/CrudView";
import SeparacaoMaterialView from "./components/SeparacaoMaterialView";
import {
  ACESSO_COMPLETO,
  ativarModoLeitura,
  podeVerAba,
  primeiraAbaLiberada,
  type Visualizacao,
} from "./modo-leitura";
import {
  SYSTEMS,
  readSsoUser,
  clearSsoFromUrl,
  goToSystem,
  checkAcesso,
  isAdminSupremo,
} from "./integration";

const SESSION_KEY = "sercamp-planejamento-user";

/** Login inicial: prioriza o SSO vindo do Portal/outros sistemas (URL) e cai na sessão salva. */
function getInitialUser(): string | null {
  const sso = readSsoUser();
  if (sso) {
    localStorage.setItem(SESSION_KEY, sso);
    return sso;
  }
  return localStorage.getItem(SESSION_KEY);
}

/** Aba inicial via deep-link do Portal (#tab=chave); cai em "dashboard" se inválida. */
const INITIAL_TAB: string = (() => {
  try {
    const m = window.location.hash.match(/[#&]tab=([^&]+)/);
    const k = m ? decodeURIComponent(m[1]) : "";
    return k && TABS.some((t) => t.key === k) ? k : "dashboard";
  } catch {
    return "dashboard";
  }
})();

export default function App() {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [user, setUser] = useState<string | null>(getInitialUser);

  useEffect(() => {
    clearSsoFromUrl();
  }, []);
  const [active, setActive] = useState<string>(INITIAL_TAB);
  const [refs, setRefs] = useState<Refs>({ colaboradores: [], obras: [] });
  const [refreshKey, setRefreshKey] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  // Só administradores excluem registros (regra do Portal) — vem do /api/me.
  const [podeExcluir, setPodeExcluir] = useState(false);
  const [displayName, setDisplayName] = useState<string | null>(null);

  // Portão de acesso (Portal): verifica se o usuário pode usar ESTE sistema.
  const [access, setAccess] = useState<"checking" | "allowed" | "blocked" | "error">("checking");
  /* No celular o menu vira uma faixa que rola de lado: isto mantem a aba
     aberta a vista (ver src/faixa-abas.tsx). */
  const navRef = useFaixaDeAbas(active, access === "allowed");
  const [accessTry, setAccessTry] = useState(0); // incrementa para tentar de novo após falha de rede.
  // Somente visualização (cadastro do Portal): abas liberadas e nada de gravar.
  const [vis, setVis] = useState<Visualizacao>(ACESSO_COMPLETO);

  // Roda toda vez que há um usuário atual (SSO novo ou sessão restaurada).
  useEffect(() => {
    if (!user) {
      setAccess("checking");
      return;
    }
    // Admin Supremo: sempre liberado, nem chega a depender da rede.
    if (isAdminSupremo(user)) {
      setAccess("allowed");
      return;
    }
    let vivo = true;
    setAccess("checking");
    checkAcesso(user)
      .then((r) => {
        if (!vivo) return;
        setVis({ somenteLeitura: r.somenteLeitura, abas: r.abas });
        setAccess(r.allowed ? "allowed" : "blocked");
      })
      .catch(() => {
        if (vivo) setAccess("error");
      });
    return () => {
      vivo = false;
    };
  }, [user, accessTry]);

  // Resolve se o usuário logado é administrador (define quem vê/gerencia "Usuários").
  useEffect(() => {
    setSessionUser(user ?? "");
    if (!user) {
      setIsAdmin(false);
      setPodeExcluir(false);
      setDisplayName(null);
      return;
    }
    api
      .me(user)
      .then((r) => {
        setIsAdmin(!!r.isAdmin);
        setPodeExcluir(!!r.podeExcluir);
        setDisplayName(r.nome || null);
      })
      .catch(() => {
        setIsAdmin(false);
        setPodeExcluir(false);
        setDisplayName(null);
      });
  }, [user]);

  // Não-admin não acessa a aba de usuários (nem por deep-link).
  useEffect(() => {
    if (active === "usuarios" && !isAdmin) setActive("dashboard");
  }, [active, isAdmin]);

  // Somente visualização: faixa de aviso, sem botões de gravar e sem gravação.
  useEffect(() => {
    ativarModoLeitura(access === "allowed" && vis.somenteLeitura);
  }, [access, vis.somenteLeitura]);

  // Aba fora das liberadas (inclusive por deep-link) cai na primeira liberada.
  // Dashboard e Agenda não estão em TABS (são fixas no menu), mas o Portal
  // libera/esconde as duas pelas mesmas chaves.
  const verAba = (k: string) =>
    podeVerAba(vis, k) && !(vis.somenteLeitura && (k === "usuarios" || k === "solicitar_atualizacao"));
  useEffect(() => {
    if (!verAba(active)) {
      const primeira = primeiraAbaLiberada(
        vis,
        ["dashboard", "agenda", ...TABS.map((t) => t.key)].filter(verAba),
      );
      if (primeira) setActive(primeira);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, vis]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggleTheme = useCallback(
    () => setTheme((t) => (t === "dark" ? "light" : "dark")),
    [],
  );

  const loadRefs = useCallback(async () => {
    try {
      const [colaboradores, obras] = await Promise.all([
        api.list("colaboradores") as Promise<Row[]>,
        api.list("obras") as Promise<Row[]>,
      ]);
      // As listas de referência já saem daqui em ordem alfabética (src/ordem.ts):
      // é delas que vivem os selects, os filtros e as linhas da Agenda.
      setRefs({
        colaboradores: ordenarPorNome(colaboradores, (c) => c.nome),
        obras: ordenarPorNome(obras, (o) => o.nome),
      });
    } catch {
      /* mostrado nas telas individuais */
    }
  }, []);

  useEffect(() => {
    if (user) loadRefs();
  }, [user, loadRefs]);

  const onDataChanged = useCallback(() => {
    loadRefs();
    setRefreshKey((k) => k + 1);
  }, [loadRefs]);

  function login(name: string) {
    localStorage.setItem(SESSION_KEY, name);
    setUser(name);
  }

  function logout() {
    localStorage.removeItem(SESSION_KEY);
    encerrarSessao();
    setUser(null);
    setVis(ACESSO_COMPLETO);
  }

  if (!user) {
    return <Login onLogin={login} theme={theme} onToggleTheme={toggleTheme} />;
  }

  // Portão de acesso: enquanto verifica, mostra "Verificando acesso..." (não pisca o app).
  if (access === "checking") {
    return <AccessScreen variant="checking" onPortal={() => goToSystem(SYSTEMS.portal, user)} />;
  }
  // Bloqueado: anula a sessão e mostra a tela cheia (não renderiza o app).
  if (access === "blocked") {
    return (
      <AccessScreen
        variant="blocked"
        onPortal={() => {
          localStorage.removeItem(SESSION_KEY);
          encerrarSessao();
          goToSystem(SYSTEMS.portal, user);
        }}
      />
    );
  }
  // Falha de rede ao consultar o Portal: não libera; permite tentar de novo.
  if (access === "error") {
    return (
      <AccessScreen
        variant="error"
        onPortal={() => goToSystem(SYSTEMS.portal, user)}
        onRetry={() => setAccessTry((n) => n + 1)}
      />
    );
  }

  const tab = TABS.find((t) => t.key === active);
  const title =
    active === "dashboard" ? "Dashboard" : active === "agenda" ? "Agenda" : tab?.label ?? "";
  const subtitle =
    active === "dashboard"
      ? "Visão geral do Planejamento de Obras"
      : active === "agenda"
        ? "Cronograma de serviços internos e externos"
        : tab?.subtitle ?? "";

  return (
    <PermissoesProvider value={podeExcluir}>
      <AvisoSistema />
      <AvisoReuniao usuario={user} onAbrir={() => setActive("solicitar_atualizacao")} />
      <div className="app">
        <AnimatedBackground />

        <aside className="sidebar">
          <div className="brand">
            <img src={logoBranca} alt="SERCAMP" className="brand-logo-img" />
          </div>

          <nav className="nav" ref={navRef}>
            {verAba("dashboard") && (
              <button
                className={`nav-item ${active === "dashboard" ? "active" : ""}`}
                onClick={() => setActive("dashboard")}
              >
                <span className="nav-icon">
                  <IconDashboard size={19} />
                </span>
                Dashboard
              </button>
            )}
            {verAba("agenda") && (
              <button
                className={`nav-item ${active === "agenda" ? "active" : ""}`}
                onClick={() => setActive("agenda")}
              >
                <span className="nav-icon">
                  <IconCalendar size={19} />
                </span>
                Agenda
              </button>
            )}
            {TABS.filter((t) => (t.key !== "usuarios" || isAdmin) && verAba(t.key)).map((t) => {
              const Icon = ICONS[t.icon];
              return (
                <button
                  key={t.key}
                  className={`nav-item ${active === t.key ? "active" : ""}`}
                  onClick={() => setActive(t.key)}
                >
                  <span className="nav-icon">
                    <Icon size={19} />
                  </span>
                  {t.label}
                </button>
              );
            })}
          </nav>

        </aside>

        <main className="content">
          <header className="topbar">
            <div>
              <h1>{title}</h1>
              <p className="muted">{subtitle}</p>
            </div>

            <div className="topbar-actions">
              <button
                className="theme-toggle"
                onClick={() => goToSystem(SYSTEMS.portal, user)}
                title="Portal SERCAMP"
              >
                <IconHome size={18} />
              </button>
              <button
                className="theme-toggle"
                onClick={toggleTheme}
                title={theme === "dark" ? "Tema claro" : "Tema escuro"}
              >
                {theme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
              </button>
              <div className="user-chip">
                <span className="user-avatar">
                  {user.charAt(0).toUpperCase()}
                </span>
                <span className="user-name">{displayName || user}</span>
              </div>
              <button className="theme-toggle" onClick={logout} title="Sair">
                <IconLogout size={18} />
              </button>
            </div>
          </header>

          <section className="view">
            {!verAba(active) ? null : active === "dashboard" ? (
              <Dashboard refreshKey={refreshKey} />
            ) : active === "agenda" ? (
              <AgendaView refs={refs} onDataChanged={onDataChanged} />
            ) : active === "atividades" && tab ? (
              <AtividadesView
                tab={tab}
                refs={refs}
                onDataChanged={onDataChanged}
                // O quadro precisa saber: cartão arrastável não é botão, e o
                // filtro de src/modo-leitura.ts não tem como escondê-lo.
                somenteLeitura={vis.somenteLeitura}
              />
            ) : active === "separacao_material" ? (
              <SeparacaoMaterialView onDataChanged={onDataChanged} />
            ) : tab?.key === "solicitar_atualizacao" ? (
              // A aba que existe em todos os sistemas: o pedido vai para o
              // sistema de Desenvolvimento (ver server/solicitar-atualizacao.ts).
              <SolicitarAtualizacaoView usuario={user} />
            ) : tab ? (
              <CrudView key={tab.key} tab={tab} refs={refs} onDataChanged={onDataChanged} />
            ) : null}
          </section>
        </main>
      </div>
    </PermissoesProvider>
  );
}

/**
 * Tela cheia do portão de acesso (Portal). Cobre três estados:
 *  - checking: "Verificando acesso..." (não pisca o app).
 *  - blocked: usuário sem permissão neste sistema.
 *  - error: falha de rede ao consultar o Portal (permite tentar de novo).
 */
function AccessScreen({
  variant,
  onPortal,
  onRetry,
}: {
  variant: "checking" | "blocked" | "error";
  onPortal: () => void;
  onRetry?: () => void;
}) {
  const titulo =
    variant === "checking"
      ? "Verificando acesso..."
      : variant === "blocked"
        ? "Acesso não autorizado"
        : "Não foi possível verificar o acesso";
  const mensagem =
    variant === "checking"
      ? "Aguarde enquanto confirmamos sua permissão neste sistema."
      : variant === "blocked"
        ? "Você não tem acesso a este sistema. Fale com o Admin Supremo."
        : "Não conseguimos falar com o Portal para confirmar seu acesso. Verifique sua conexão e tente novamente.";

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
      }}
    >
      <AnimatedBackground />
      <div
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 440,
          textAlign: "center",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 18,
          padding: "40px 32px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
        }}
      >
        <img
          src={logoBranca}
          alt="SERCAMP"
          style={{ height: 44, marginBottom: 24, filter: "drop-shadow(0 2px 6px rgba(0,0,0,.25))" }}
        />
        <h1 style={{ fontSize: 22, margin: "0 0 10px", color: "var(--text)" }}>{titulo}</h1>
        <p style={{ margin: "0 0 24px", color: "var(--muted, #888)", lineHeight: 1.5 }}>{mensagem}</p>

        {variant === "checking" ? (
          <div className="muted" style={{ fontSize: 14 }}>Verificando acesso...</div>
        ) : (
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            {variant === "error" && onRetry && (
              <button className="btn-primary" onClick={onRetry}>
                Tentar novamente
              </button>
            )}
            <button className="theme-toggle" style={{ width: "auto", padding: "0 18px", gap: 8 }} onClick={onPortal}>
              <IconHome size={16} />
              Voltar ao Portal
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
